import { TableFieldType } from "../table-field/table-field.model";
import {
  ChartAggregation,
  ChartType,
  dropdownNumericMap,
} from "./chart.model";

/**
 * Self-contained SVG preview renderers, ported from the chart_design_studio.html
 * prototype. This is a sanity-check preview only — fidelity to the eventual
 * server-side (Matplotlib/PDF) output is explicitly not a goal.
 */

export interface PreviewColumn {
  columnKey: string;
  name: string;
  type: TableFieldType | number;
  values?: string[];
  /** JSON string of option → numeric value, when the dropdown has one */
  valueMap?: string | Record<string, number> | null;
}

export interface ChartPreviewConfig {
  chartType: ChartType;
  categoryColumnKey: string;
  yAxisColumnKey?: string;
  aggregation: ChartAggregation;
  series: { valueColumnKey: string; label?: string; color?: string }[];
  bandEdges: number[];
  bandColors: string[];
  showLegend: boolean;
  title?: string;
  name?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  zAxisLabel?: string;
  widthPx: number;
  heightPx: number;
  /** SURFACE_3D preview only — camera yaw in degrees */
  viewAzimuth?: number;
  /** SURFACE_3D preview only — camera pitch above the plane, degrees (5–85) */
  viewElevation?: number;
  /** banded chart preview only — 0.15–1 fill opacity of the surface / cells */
  surfaceOpacity?: number;
  /** banded chart preview only — smooth (Catmull-Rom) interpolation between points */
  smoothSurface?: boolean;
}

interface PreviewRange {
  min: number;
  max: number;
  integer: boolean;
}

export type PreviewData =
  | {
      kind: "rows";
      rows: { category: string | number; [key: string]: string | number }[];
      catCol: PreviewColumn;
      seriesCols: {
        valueColumnKey: string;
        label?: string;
        color?: string;
        col: PreviewColumn;
      }[];
    }
  | {
      kind: "grid";
      xs: number[];
      ys: number[];
      points: { x: number; y: number; z: number }[];
      xCol: PreviewColumn;
      yCol: PreviewColumn;
      zCol: PreviewColumn;
    }
  | null;

export interface ValueRange {
  min: number;
  max: number;
}

function isNumeric(col: PreviewColumn): boolean {
  return (
    +col.type === TableFieldType.INTEGER || +col.type === TableFieldType.DECIMAL
  );
}

function axisRangeOf(col: PreviewColumn): PreviewRange {
  return {
    min: 0,
    max: 100,
    integer: +col.type === TableFieldType.INTEGER,
  };
}

function samplePool(col: PreviewColumn): string[] {
  if (col.values && col.values.length > 0) {
    return col.values;
  }
  return ["A", "B", "C", "D", "E", "F"];
}

function roundValue(v: number, integer: boolean): number {
  return integer ? Math.round(v) : Math.round(v * 1000) / 1000;
}

function sampleValue(
  col: PreviewColumn,
  i: number,
  range: ValueRange,
): string | number {
  if (!isNumeric(col)) {
    const pool = samplePool(col);
    return pool[i % pool.length];
  }
  const v = range.min + Math.random() * (range.max - range.min);
  return roundValue(v, +col.type === TableFieldType.INTEGER);
}

function linspace(min: number, max: number, n: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    out.push(min + (max - min) * (i / (n - 1)));
  }
  return out;
}

function col(cols: PreviewColumn[], key: string): PreviewColumn | null {
  return cols.find((c) => c.columnKey === key) ?? null;
}

/**
 * Axis sample points for a banded chart. A fully-mapped dropdown contributes its
 * assigned numeric values; a numeric column gets an evenly spaced synthetic
 * range.
 */
function axisValues(c: PreviewColumn, gridN: number): number[] {
  const dmap = dropdownNumericMap(c);
  if (dmap) {
    return Array.from(new Set(Object.values(dmap))).sort((a, b) => a - b);
  }
  const r = axisRangeOf(c);
  return linspace(r.min, r.max, gridN);
}

export function generatePreviewData(
  cfg: ChartPreviewConfig,
  cols: PreviewColumn[],
  rowCount: number,
  needsYAxis: boolean,
  valueRange: ValueRange,
): PreviewData {
  const catCol = col(cols, cfg.categoryColumnKey);
  if (!catCol) {
    return null;
  }
  const vMin = Number.isFinite(valueRange?.min) ? valueRange.min : 0;
  const vMax = Number.isFinite(valueRange?.max) ? valueRange.max : vMin + 1;
  const range: ValueRange = { min: Math.min(vMin, vMax), max: Math.max(vMin, vMax) };

  if (needsYAxis) {
    const xCol = catCol;
    const yCol = col(cols, cfg.yAxisColumnKey);
    const zCol = col(cols, cfg.series[0]?.valueColumnKey);
    if (!xCol || !yCol || !zCol) {
      return null;
    }
    const gridN = Math.max(3, Math.min(10, rowCount || 6));
    const xs = axisValues(xCol, gridN);
    const ys = axisValues(yCol, gridN);
    const xr = { min: xs[0], max: xs[xs.length - 1] };
    const yr = { min: ys[0], max: ys[ys.length - 1] };
    const f1 = 1 + Math.random() * 1.5;
    const f2 = 1 + Math.random() * 1.5;
    const p1 = Math.random() * Math.PI * 2;
    const p2 = Math.random() * Math.PI * 2;
    const span = range.max - range.min || 1;
    const base = range.min + span * (0.35 + Math.random() * 0.15);
    const amp = span * 0.5;
    const integer = +zCol.type === TableFieldType.INTEGER;
    const points: { x: number; y: number; z: number }[] = [];
    xs.forEach((x) => {
      ys.forEach((y) => {
        const nx = (x - xr.min) / (xr.max - xr.min || 1);
        const ny = (y - yr.min) / (yr.max - yr.min || 1);
        let z =
          base +
          amp *
            Math.sin(nx * Math.PI * f1 + p1) *
            Math.cos(ny * Math.PI * f2 + p2);
        z = Math.max(range.min, Math.min(range.max, z));
        // z is kept in real value units so it maps directly onto band edges
        points.push({ x, y, z: roundValue(z, integer) });
      });
    });
    return { kind: "grid", xs, ys, points, xCol, yCol, zCol };
  }

  const seriesCols = cfg.series
    .filter((s) => s.valueColumnKey)
    .map((s) => ({ ...s, col: col(cols, s.valueColumnKey) }))
    .filter((s) => s.col != null) as {
    valueColumnKey: string;
    label?: string;
    color?: string;
    col: PreviewColumn;
  }[];
  if (seriesCols.length === 0) {
    return null;
  }
  const n = Math.max(3, Math.min(30, rowCount || 8));
  const rows: { category: string | number; [key: string]: string | number }[] =
    [];
  for (let i = 0; i < n; i++) {
    const row: { category: string | number; [key: string]: string | number } = {
      category: sampleValue(catCol, i, range),
    };
    seriesCols.forEach((s, si) => {
      row[s.valueColumnKey] = sampleValue(s.col, i + si * 7 + 1, range);
    });
    rows.push(row);
  }
  return { kind: "rows", rows, catCol, seriesCols };
}

function esc(s: unknown): string {
  return String(s).replace(
    /[&<>]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] as string,
  );
}

function svgOpen(w: number, h: number): string {
  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">`;
}

function aggregate(
  rows: { category: string | number; [key: string]: string | number }[],
  seriesCols: { valueColumnKey: string }[],
  aggregation: ChartAggregation,
): { category: string; values: number[] }[] {
  const byCat = new Map<string, number[][]>();
  rows.forEach((r) => {
    const key = String(r.category);
    if (!byCat.has(key)) {
      byCat.set(
        key,
        seriesCols.map(() => []),
      );
    }
    const arrs = byCat.get(key);
    seriesCols.forEach((s, i) =>
      arrs[i].push(Number(r[s.valueColumnKey]) || 0),
    );
  });
  const reduce = (arr: number[]): number => {
    if (!arr.length) {
      return 0;
    }
    switch (aggregation) {
      case "SUM":
        return arr.reduce((a, b) => a + b, 0);
      case "AVG":
        return arr.reduce((a, b) => a + b, 0) / arr.length;
      case "COUNT":
        return arr.length;
      case "MIN":
        return Math.min(...arr);
      case "MAX":
        return Math.max(...arr);
      default:
        return arr.reduce((a, b) => a + b, 0);
    }
  };
  if (aggregation === "NONE") {
    return rows.map((r) => ({
      category: String(r.category),
      values: seriesCols.map((s) => Number(r[s.valueColumnKey]) || 0),
    }));
  }
  return Array.from(byCat.entries()).map(([key, arrs]) => ({
    category: key,
    values: arrs.map(reduce),
  }));
}

function bandColorFor(
  z: number,
  edges: number[],
  colors: string[],
): string {
  for (let i = 0; i < colors.length; i++) {
    if (z >= edges[i] && (z <= edges[i + 1] || i === colors.length - 1)) {
      return colors[i];
    }
  }
  return colors[colors.length - 1] || "#999";
}

function renderBarOrLine(
  cfg: ChartPreviewConfig,
  data: Extract<PreviewData, { kind: "rows" }>,
  kind: "bar" | "line",
): string {
  const { seriesCols } = data;
  const grouped = aggregate(data.rows, seriesCols, cfg.aggregation);
  const W = cfg.widthPx;
  const H = cfg.heightPx;
  const m = { top: 34, right: 16, bottom: 64, left: 50 };
  const iw = W - m.left - m.right;
  const ih = H - m.top - m.bottom;
  const maxVal = Math.max(1e-6, ...grouped.flatMap((g) => g.values));
  let out = svgOpen(W, H);
  out += `<text x="${W / 2}" y="20" text-anchor="middle" font-size="13" font-weight="600">${esc(
    cfg.title || cfg.name || "",
  )}</text>`;
  for (let g = 0; g <= 4; g++) {
    const y = m.top + ih - (g / 4) * ih;
    out += `<line x1="${m.left}" y1="${y}" x2="${m.left + iw}" y2="${y}" stroke="#EAEDF1"/>`;
    out += `<text x="${m.left - 8}" y="${y + 4}" text-anchor="end" font-size="10" fill="#8891A0">${(
      (maxVal * g) /
      4
    ).toFixed(1)}</text>`;
  }
  out += `<line x1="${m.left}" y1="${m.top + ih}" x2="${m.left + iw}" y2="${m.top + ih}" stroke="#C7CCD4"/>`;

  const bandW = iw / Math.max(1, grouped.length);
  if (kind === "bar") {
    const sN = seriesCols.length;
    const subW = bandW / (sN + 1);
    grouped.forEach((g, ci) => {
      g.values.forEach((v, si) => {
        const barH = (v / maxVal) * ih;
        const x = m.left + ci * bandW + subW * (si + 0.5);
        const y = m.top + ih - barH;
        out += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${(
          subW * 0.85
        ).toFixed(1)}" height="${Math.max(0, barH).toFixed(1)}" fill="${
          seriesCols[si]?.color || "#2F5FDB"
        }" rx="2"/>`;
      });
    });
  } else {
    seriesCols.forEach((s, si) => {
      const pts = grouped.map((g, ci) => {
        const x = m.left + ci * bandW + bandW / 2;
        const y = m.top + ih - (g.values[si] / maxVal) * ih;
        return [x, y];
      });
      out += `<polyline points="${pts
        .map((p) => p.join(","))
        .join(" ")}" fill="none" stroke="${s.color || "#2F5FDB"}" stroke-width="2.2"/>`;
      pts.forEach(
        (p) =>
          (out += `<circle cx="${p[0]}" cy="${p[1]}" r="3.2" fill="${
            s.color || "#2F5FDB"
          }"/>`),
      );
    });
  }

  grouped.forEach((g, ci) => {
    const x = m.left + ci * bandW + bandW / 2;
    out += `<text x="${x}" y="${m.top + ih + 16}" text-anchor="end" font-size="10.5" fill="#5B6472" transform="rotate(-35 ${x} ${
      m.top + ih + 16
    })">${esc(g.category)}</text>`;
  });

  if (cfg.showLegend && seriesCols.length > 1) {
    let lx = m.left;
    const ly = H - 14;
    seriesCols.forEach((s) => {
      const label = s.label || s.col.name;
      out += `<rect x="${lx}" y="${ly - 9}" width="10" height="10" fill="${
        s.color || "#2F5FDB"
      }" rx="2"/>`;
      out += `<text x="${lx + 14}" y="${ly}" font-size="11" fill="#5B6472">${esc(
        label,
      )}</text>`;
      lx += 16 + label.length * 6.2 + 14;
    });
  }
  out += "</svg>";
  return out;
}

function renderPie(
  cfg: ChartPreviewConfig,
  data: Extract<PreviewData, { kind: "rows" }>,
): string {
  const grouped = aggregate(data.rows, data.seriesCols, cfg.aggregation);
  const W = cfg.widthPx;
  const H = cfg.heightPx;
  const cx = W * 0.36;
  const cy = H / 2;
  const r = Math.min(W * 0.32, H / 2 - 30);
  const total = grouped.reduce((a, g) => a + g.values[0], 0) || 1;
  let angle = -Math.PI / 2;
  let out = svgOpen(W, H);
  out += `<text x="${W / 2}" y="20" text-anchor="middle" font-size="13" font-weight="600">${esc(
    cfg.title || cfg.name || "",
  )}</text>`;
  const palette = [
    "#2F5FDB",
    "#C0504D",
    "#9BBB59",
    "#8064A2",
    "#E0A030",
    "#3FA7A2",
  ];
  grouped.forEach((g, i) => {
    const frac = g.values[0] / total;
    const a0 = angle;
    const a1 = angle + frac * Math.PI * 2;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const large = a1 - a0 > Math.PI ? 1 : 0;
    out += `<path d="M${cx},${cy} L${x0.toFixed(1)},${y0.toFixed(1)} A${r},${r} 0 ${large} 1 ${x1.toFixed(
      1,
    )},${y1.toFixed(1)} Z" fill="${palette[i % palette.length]}" stroke="#fff" stroke-width="1.5"/>`;
    angle = a1;
  });
  if (cfg.showLegend) {
    let ly = 46;
    grouped.forEach((g, i) => {
      out += `<rect x="${W * 0.68}" y="${ly - 9}" width="10" height="10" fill="${
        palette[i % palette.length]
      }" rx="2"/>`;
      out += `<text x="${W * 0.68 + 14}" y="${ly}" font-size="11" fill="#5B6472">${esc(
        g.category,
      )} (${g.values[0].toFixed(1)})</text>`;
      ly += 18;
    });
  }
  out += "</svg>";
  return out;
}

function renderSurface3D(
  cfg: ChartPreviewConfig,
  data: Extract<PreviewData, { kind: "grid" }>,
): string {
  const { xs, ys, points, xCol, yCol, zCol } = data;
  const W = cfg.widthPx;
  const H = cfg.heightPx;
  const originX = W * 0.5;
  const originY = H * 0.52;
  const scale = Math.min(W, H) * 0.58;
  const heightScale = 0.6;
  const az = ((cfg.viewAzimuth ?? 45) * Math.PI) / 180;
  const el = ((cfg.viewElevation ?? 30) * Math.PI) / 180;
  const opacity = Math.max(0.15, Math.min(1, cfg.surfaceOpacity ?? 1));

  const zLookup = new Map<string, number>();
  points.forEach((p) => zLookup.set(p.x + "|" + p.y, p.z));
  const xMin = xs[0];
  const xSpan = xs[xs.length - 1] - xs[0] || 1;
  const yMin = ys[0];
  const ySpan = ys[ys.length - 1] - ys[0] || 1;

  const nx = xs.length;
  const ny = ys.length;
  const zGrid: number[][] = xs.map((x) =>
    ys.map((y) => zLookup.get(x + "|" + y) ?? 0),
  );

  const smooth = cfg.smoothSurface !== false;
  // sub-divisions per original cell — more when the base grid is coarse
  const sub = smooth ? (nx <= 6 ? 6 : 4) : 1;
  const fnx = (nx - 1) * sub;
  const fny = (ny - 1) * sub;

  const fx = (fi: number) => xMin + (xSpan * fi) / fnx;
  const fy = (fj: number) => yMin + (ySpan * fj) / fny;
  const fz = (fi: number, fj: number) =>
    smooth
      ? sampleGrid(zGrid, nx, ny, fi / sub, fj / sub)
      : zGrid[Math.round(fi / sub)][Math.round(fj / sub)];

  // z-range over the *sampled* surface (a spline can overshoot the samples)
  let zMin = Infinity;
  let zMax = -Infinity;
  for (let fi = 0; fi <= fnx; fi++) {
    for (let fj = 0; fj <= fny; fj++) {
      const z = fz(fi, fj);
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }
  }
  const zSpan = zMax - zMin || 1;

  const proj = (
    x: number,
    y: number,
    z: number,
  ): { sx: number; sy: number; depth: number } => {
    const ncx = (x - xMin) / xSpan - 0.5;
    const ncy = (y - yMin) / ySpan - 0.5;
    const ncz = (z - zMin) / zSpan - 0.5;
    const rx = ncx * Math.cos(az) - ncy * Math.sin(az);
    const ry = ncx * Math.sin(az) + ncy * Math.cos(az);
    const sx = originX + rx * scale;
    const sy =
      originY +
      ry * Math.sin(el) * scale -
      ncz * Math.cos(el) * heightScale * scale;
    return { sx, sy, depth: ry };
  };

  const quads: {
    pts: { sx: number; sy: number }[];
    avgZ: number;
    depth: number;
  }[] = [];
  for (let fi = 0; fi < fnx; fi++) {
    for (let fj = 0; fj < fny; fj++) {
      const cell = [
        [fi, fj],
        [fi + 1, fj],
        [fi + 1, fj + 1],
        [fi, fj + 1],
      ];
      const zs = cell.map(([a, b]) => fz(a, b));
      const avgZ = zs.reduce((s, v) => s + v, 0) / zs.length;
      const pr = cell.map(([a, b], k) => proj(fx(a), fy(b), zs[k]));
      const depth = pr.reduce((s, p) => s + p.depth, 0) / pr.length;
      quads.push({ pts: pr, avgZ, depth });
    }
  }
  // painter's algorithm: draw the quads farthest from the camera first
  quads.sort((a, b) => a.depth - b.depth);

  let out = svgOpen(W, H);
  out += `<text x="${W / 2}" y="20" text-anchor="middle" font-size="13" font-weight="600">${esc(
    cfg.title || cfg.name || "",
  )}</text>`;
  // faceted: white mesh lines; smooth: paint each quad with a hairline of its
  // own colour so sub-facets fuse into a continuous surface
  out += `<g fill-opacity="${opacity}">`;
  quads.forEach((q) => {
    const d = q.pts.map((p) => `${p.sx.toFixed(1)},${p.sy.toFixed(1)}`).join(" ");
    const fill = bandColorFor(q.avgZ, cfg.bandEdges, cfg.bandColors);
    // faceted: translucent white mesh lines; smooth: hairline in the band colour.
    // Keep colours as hex + a separate opacity attr so Batik (SVG 1.1) can parse
    // them on the server-side render — it does not accept rgba() here.
    const stroke = smooth ? fill : "#ffffff";
    const strokeOpacity = smooth ? opacity : 0.5;
    out += `<polygon points="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${
      smooth ? 0.6 : 1
    }" stroke-opacity="${strokeOpacity}"/>`;
  });
  out += `</g>`;

  const xLbl = cfg.xAxisLabel || xCol.name;
  const yLbl = cfg.yAxisLabel || yCol.name;
  const zLbl = cfg.zAxisLabel || zCol.name;

  // ---- reference axes: X / Y / Z through the origin corner, with the 0 level --
  const xA = xs[0];
  const xB = xs[xs.length - 1];
  const yA = ys[0];
  const yB = ys[ys.length - 1];
  // draw the horizontal axes on the z = 0 plane when 0 is in range, else on the floor
  const zFloor = zMin <= 0 && 0 <= zMax ? 0 : zMin;
  const axisLine = (
    p1: { sx: number; sy: number },
    p2: { sx: number; sy: number },
  ): string =>
    `<line x1="${p1.sx.toFixed(1)}" y1="${p1.sy.toFixed(1)}" x2="${p2.sx.toFixed(
      1,
    )}" y2="${p2.sy.toFixed(1)}" stroke="#5B6472" stroke-width="1.3" stroke-opacity="0.85"/>`;
  const axisTag = (p: { sx: number; sy: number }, t: string): string =>
    `<text x="${p.sx.toFixed(1)}" y="${p.sy.toFixed(
      1,
    )}" font-size="10.5" font-weight="600" fill="#5B6472" text-anchor="middle">${esc(
      t,
    )}</text>`;
  const zeroMark = (p: { sx: number; sy: number }, t: string): string =>
    `<circle cx="${p.sx.toFixed(1)}" cy="${p.sy.toFixed(
      1,
    )}" r="2.6" fill="#C0504D"/><text x="${(p.sx + 5).toFixed(1)}" y="${(
      p.sy - 4
    ).toFixed(1)}" font-size="9.5" fill="#C0504D">${esc(t)}</text>`;

  const oXY = proj(xA, yA, zFloor);
  const xEnd = proj(xB, yA, zFloor);
  const yEnd = proj(xA, yB, zFloor);
  const zBot = proj(xA, yA, zMin);
  const zTop = proj(xA, yA, zMax);
  out += axisLine(oXY, xEnd) + axisLine(oXY, yEnd) + axisLine(zBot, zTop);
  out += axisTag({ sx: xEnd.sx, sy: xEnd.sy + 14 }, xLbl);
  out += axisTag({ sx: yEnd.sx, sy: yEnd.sy + 14 }, yLbl);
  out += axisTag({ sx: zTop.sx, sy: zTop.sy - 8 }, zLbl);

  if (zMin <= 0 && 0 <= zMax) {
    // dashed outline of the z = 0 plane so the zero level reads in 3D
    const plane = [
      proj(xA, yA, 0),
      proj(xB, yA, 0),
      proj(xB, yB, 0),
      proj(xA, yB, 0),
    ]
      .map((p) => `${p.sx.toFixed(1)},${p.sy.toFixed(1)}`)
      .join(" ");
    out += `<polygon points="${plane}" fill="#C0504D" fill-opacity="0.06" stroke="#C0504D" stroke-opacity="0.4" stroke-width="1" stroke-dasharray="4 3"/>`;
    out += zeroMark(proj(xA, yA, 0), "z=0");
  }
  if (xA <= 0 && 0 <= xB) {
    out += zeroMark(proj(0, yA, zFloor), "x=0");
  }
  if (yA <= 0 && 0 <= yB) {
    out += zeroMark(proj(xA, 0, zFloor), "y=0");
  }

  out += `<text x="${originX}" y="${H - 10}" text-anchor="middle" font-size="11" fill="#5B6472">${esc(
    xLbl,
  )} · ${esc(yLbl)} · ${esc(zLbl)}</text>`;

  if (cfg.showLegend) {
    let lx = 14;
    const ly = H - 30;
    for (let i = 0; i < cfg.bandColors.length; i++) {
      const label = `${(cfg.bandEdges[i] ?? 0).toFixed(2)}–${(
        cfg.bandEdges[i + 1] ?? 0
      ).toFixed(2)}`;
      out += `<rect x="${lx}" y="${ly - 9}" width="10" height="10" fill="${cfg.bandColors[i]}" rx="2"/>`;
      out += `<text x="${lx + 14}" y="${ly}" font-size="10.5" fill="#5B6472">${esc(
        label,
      )}</text>`;
      lx += 14 + label.length * 6 + 16;
    }
  }
  out += "</svg>";
  return out;
}

/** 1D Catmull-Rom spline segment between p1 and p2 (t in 0..1). */
function catmullRom(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number,
): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
  );
}

/**
 * Smoothly sample a z lattice at fractional grid indices (u along x, v along y),
 * bicubic via a tensor product of Catmull-Rom splines. Edges clamp.
 */
function sampleGrid(
  zGrid: number[][],
  nx: number,
  ny: number,
  u: number,
  v: number,
): number {
  const ci = Math.max(0, Math.min(nx - 2, Math.floor(u)));
  const cj = Math.max(0, Math.min(ny - 2, Math.floor(v)));
  const tu = u - ci;
  const tv = v - cj;
  const gi = (k: number) => Math.max(0, Math.min(nx - 1, k));
  const gj = (k: number) => Math.max(0, Math.min(ny - 1, k));
  const rowAt = (jj: number) =>
    catmullRom(
      zGrid[gi(ci - 1)][jj],
      zGrid[gi(ci)][jj],
      zGrid[gi(ci + 1)][jj],
      zGrid[gi(ci + 2)][jj],
      tu,
    );
  return catmullRom(
    rowAt(gj(cj - 1)),
    rowAt(gj(cj)),
    rowAt(gj(cj + 1)),
    rowAt(gj(cj + 2)),
    tv,
  );
}

function bilerp(
  points: Map<string, number>,
  xs: number[],
  ys: number[],
  x: number,
  y: number,
): number {
  let i = 0;
  while (i < xs.length - 2 && xs[i + 1] < x) i++;
  let j = 0;
  while (j < ys.length - 2 && ys[j + 1] < y) j++;
  const x0 = xs[i];
  const x1 = xs[i + 1];
  const y0 = ys[j];
  const y1 = ys[j + 1];
  const z00 = points.get(x0 + "|" + y0) ?? 0;
  const z10 = points.get(x1 + "|" + y0) ?? 0;
  const z01 = points.get(x0 + "|" + y1) ?? 0;
  const z11 = points.get(x1 + "|" + y1) ?? 0;
  const tx = x1 > x0 ? (x - x0) / (x1 - x0) : 0;
  const ty = y1 > y0 ? (y - y0) / (y1 - y0) : 0;
  const zTop = z00 + (z10 - z00) * tx;
  const zBot = z01 + (z11 - z01) * tx;
  return zTop + (zBot - zTop) * ty;
}

function renderAreaBanded2D(
  cfg: ChartPreviewConfig,
  data: Extract<PreviewData, { kind: "grid" }>,
): string {
  const { xs, ys, points, xCol, yCol } = data;
  const zLookup = new Map<string, number>();
  points.forEach((p) => zLookup.set(p.x + "|" + p.y, p.z));
  const smooth = cfg.smoothSurface !== false;
  const nx = xs.length;
  const ny = ys.length;
  const zGrid: number[][] = xs.map((x) =>
    ys.map((y) => zLookup.get(x + "|" + y) ?? 0),
  );
  const sampleZ = (x: number, y: number): number => {
    if (!smooth) {
      return bilerp(zLookup, xs, ys, x, y);
    }
    const u = ((x - xs[0]) / (xs[nx - 1] - xs[0] || 1)) * (nx - 1);
    const v = ((y - ys[0]) / (ys[ny - 1] - ys[0] || 1)) * (ny - 1);
    return sampleGrid(zGrid, nx, ny, u, v);
  };
  const W = cfg.widthPx;
  const H = cfg.heightPx;
  const m = { top: 34, right: 16, bottom: 50, left: 56 };
  const iw = W - m.left - m.right;
  const ih = H - m.top - m.bottom;
  const STEPS_X = 48;
  const STEPS_Y = 32;
  let out = svgOpen(W, H);
  out += `<text x="${W / 2}" y="20" text-anchor="middle" font-size="13" font-weight="600">${esc(
    cfg.title || cfg.name || "",
  )}</text>`;
  const cellW = iw / STEPS_X;
  const cellH = ih / STEPS_Y;
  const opacity = Math.max(0.15, Math.min(1, cfg.surfaceOpacity ?? 1));
  out += `<g fill-opacity="${opacity}">`;
  for (let cx = 0; cx < STEPS_X; cx++) {
    const x = xs[0] + (xs[xs.length - 1] - xs[0]) * ((cx + 0.5) / STEPS_X);
    for (let cy = 0; cy < STEPS_Y; cy++) {
      const y =
        ys[ys.length - 1] -
        (ys[ys.length - 1] - ys[0]) * ((cy + 0.5) / STEPS_Y);
      const z = sampleZ(x, y);
      const px = m.left + cx * cellW;
      const py = m.top + cy * cellH;
      out += `<rect x="${px.toFixed(1)}" y="${py.toFixed(1)}" width="${(
        cellW + 0.6
      ).toFixed(1)}" height="${(cellH + 0.6).toFixed(1)}" fill="${bandColorFor(
        z,
        cfg.bandEdges,
        cfg.bandColors,
      )}"/>`;
    }
  }
  out += `</g>`;
  out += `<rect x="${m.left}" y="${m.top}" width="${iw}" height="${ih}" fill="none" stroke="#C7CCD4"/>`;
  out += `<text x="${m.left + iw / 2}" y="${H - 14}" text-anchor="middle" font-size="11" fill="#5B6472">${esc(
    cfg.xAxisLabel || xCol.name,
  )}</text>`;
  out += `<text x="16" y="${m.top + ih / 2}" text-anchor="middle" font-size="11" fill="#5B6472" transform="rotate(-90 16 ${
    m.top + ih / 2
  })">${esc(cfg.yAxisLabel || yCol.name)}</text>`;

  if (cfg.showLegend) {
    let lx = m.left;
    const ly = m.top - 12;
    for (let i = 0; i < cfg.bandColors.length; i++) {
      const label = `${(cfg.bandEdges[i] ?? 0).toFixed(2)}–${(
        cfg.bandEdges[i + 1] ?? 0
      ).toFixed(2)}`;
      out += `<rect x="${lx}" y="${ly - 8}" width="9" height="9" fill="${cfg.bandColors[i]}" rx="1.5"/>`;
      out += `<text x="${lx + 12}" y="${ly}" font-size="9.5" fill="#5B6472">${esc(
        label,
      )}</text>`;
      lx += 12 + label.length * 5.4 + 12;
    }
  }
  out += "</svg>";
  return out;
}

export function renderChartSvg(
  cfg: ChartPreviewConfig,
  data: PreviewData,
): string {
  if (!data) {
    return "";
  }
  try {
    if (data.kind === "rows") {
      if (cfg.chartType === "BAR") {
        return renderBarOrLine(cfg, data, "bar");
      }
      if (cfg.chartType === "LINE") {
        return renderBarOrLine(cfg, data, "line");
      }
      if (cfg.chartType === "PIE") {
        return renderPie(cfg, data);
      }
    } else if (data.kind === "grid") {
      if (cfg.chartType === "SURFACE_3D") {
        return renderSurface3D(cfg, data);
      }
      if (cfg.chartType === "AREA_BANDED_2D") {
        return renderAreaBanded2D(cfg, data);
      }
    }
  } catch {
    return "";
  }
  return "";
}
